import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col, Badge } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";

const AddSupplierModal = ({ show, onHide, onAddSupplier, onUpdateSupplier, editingSupplier }) => {
    const [newSupplier, setNewSupplier] = useState({
        name: "",
        category: "",
        isGolden: false,
        isPersonal: true,
        description: "",
        tags: [],
        phone: "",
        email: "",
        address: "",
        website: "",
        imageUrl: "https://images.unsplash.com/photo-1517438322307-e67111335449?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    });

    const [newTag, setNewTag] = useState("");
    const [errors, setErrors] = useState({});

    // Update form when editingSupplier changes
    useEffect(() => {
        if (editingSupplier) {
            setNewSupplier(editingSupplier);
        } else {
            resetForm();
        }
    }, [editingSupplier]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewSupplier({
            ...newSupplier,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const handleAddTag = () => {
        if (newTag.trim() && !newSupplier.tags.includes(newTag.trim())) {
            setNewSupplier({
                ...newSupplier,
                tags: [...newSupplier.tags, newTag.trim()],
            });
            setNewTag("");
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setNewSupplier({
            ...newSupplier,
            tags: newSupplier.tags.filter((tag) => tag !== tagToRemove),
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!newSupplier.name.trim()) newErrors.name = "שם הספק הוא שדה חובה";
        if (!newSupplier.category.trim()) newErrors.category = "קטגוריה היא שדה חובה";
        if (!newSupplier.description.trim()) newErrors.description = "תיאור הוא שדה חובה";
        if (!newSupplier.phone.trim()) newErrors.phone = "טלפון הוא שדה חובה";
        if (!newSupplier.email.trim()) newErrors.email = "אימייל הוא שדה חובה";

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (newSupplier.email && !emailRegex.test(newSupplier.email)) {
            newErrors.email = "כתובת אימייל לא תקינה";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            if (editingSupplier) {
                // Update existing supplier - make sure to preserve the ID
                const updatedSupplier = {
                    ...newSupplier,
                    id: editingSupplier.id
                };
                onUpdateSupplier(updatedSupplier);
            } else {
                // Add new supplier
                const supplierWithId = {
                    ...newSupplier,
                    id: Date.now(), // Simple way to generate a unique ID
                };
                onAddSupplier(supplierWithId);
            }
            resetForm();
            onHide();
        }
    };

    const resetForm = () => {
        setNewSupplier({
            name: "",
            category: "",
            isGolden: false,
            isPersonal: true,
            description: "",
            tags: [],
            phone: "",
            email: "",
            address: "",
            website: "",
            imageUrl: "https://images.unsplash.com/photo-1517438322307-e67111335449?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        });
        setNewTag("");
        setErrors({});
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dir="rtl">
            <Modal.Header className="eventFormHeader" style={{ flexDirection: "row", justifyContent: "space-between" }}>
                <Modal.Title className="ml-auto">{editingSupplier ? "עריכת ספק" : "הוספת ספק חדש"}</Modal.Title>
                <Button
                    variant="light"
                    className="close-button"
                    onClick={onHide}
                >
                    &times;
                </Button>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>שם הספק *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={newSupplier.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                    placeholder="הזן את שם הספק"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>קטגוריה *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="category"
                                    value={newSupplier.category}
                                    onChange={handleChange}
                                    isInvalid={!!errors.category}
                                    placeholder="הזן את הקטגוריה"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.category}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            name="isGolden"
                            label="ספק מועדף"
                            checked={newSupplier.isGolden}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>תיאור *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={newSupplier.description}
                            onChange={handleChange}
                            isInvalid={!!errors.description}
                            placeholder="תאר את הספק"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.description}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>תגיות</Form.Label>
                        <Form.Control
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            placeholder="הקלד תגית ולחץ Enter להוספה"
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddTag();
                                }
                            }}
                        />
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {newSupplier.tags.map((tag, index) => (
                                <Badge
                                    key={index}
                                    bg="light"
                                    text="dark"
                                    className="px-3 py-2 d-flex align-items-center"
                                >
                                    {tag}
                                    <FaTimes
                                        className="ms-2 cursor-pointer"
                                        onClick={() => handleRemoveTag(tag)}
                                    />
                                </Badge>
                            ))}
                        </div>
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>טלפון *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="phone"
                                    value={newSupplier.phone}
                                    onChange={handleChange}
                                    isInvalid={!!errors.phone}
                                    placeholder="הזן מספר טלפון"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.phone}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>אימייל *</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={newSupplier.email}
                                    onChange={handleChange}
                                    isInvalid={!!errors.email}
                                    placeholder="הזן כתובת אימייל"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.email}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>כתובת</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="address"
                                    value={newSupplier.address}
                                    onChange={handleChange}
                                    placeholder="הזן כתובת"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>אתר אינטרנט</Form.Label>
                                <Form.Control
                                    type="url"
                                    name="website"
                                    value={newSupplier.website}
                                    onChange={handleChange}
                                    placeholder="הזן כתובת אתר"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>כתובת תמונה</Form.Label>
                        <Form.Control
                            type="url"
                            name="imageUrl"
                            value={newSupplier.imageUrl}
                            onChange={handleChange}
                            placeholder="הזן כתובת תמונה"
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button variant="secondary" onClick={onHide}>
                            ביטול
                        </Button>
                        <Button variant="primary" type="submit">
                            {editingSupplier ? "עדכן ספק" : "הוסף ספק"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AddSupplierModal; 